/**
 * The decision: offer to install, and if so, how.
 *
 * Everything the offer depends on is a client fact — the user agent, the
 * display mode, localStorage, an event Chromium may or may not have fired —
 * and this page is prerendered. So the hook returns `hidden` on its first
 * render, always, on the server and on the client alike, and only then does
 * an effect look at the world and decide. That discipline is required of
 * every hook in a prerendered app, not just this one: the first client render
 * has to reproduce the build's markup exactly, and a render that consults
 * storage cannot. The one render-time read is ../lib/install-prompt.ts's
 * store, through useSyncExternalStore, whose server snapshot is what
 * hydration renders with — and `hidden` does not depend on it until `due` is
 * true.
 *
 * The policy is the shared one (../lib/install-state.ts); the panel is the
 * app's. apps/retells/src/install/InstallOffer.tsx is the reference consumer.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  canPromptNatively,
  promptInstall,
  subscribeInstallability,
  wasInstalledThisSession,
  type InstallOutcome,
} from "../lib/install-prompt";
import {
  currentPlatform,
  isRunningInstalled,
  type InstallPlatform,
} from "../lib/install-platform";
import {
  INSTALL_TUNING,
  isOfferDue,
  type InstallStateStore,
} from "../lib/install-state";

export type InstallOffer =
  /** Say nothing. */
  | { readonly kind: "hidden" }
  /** One button; the browser owns the dialog behind it. */
  | { readonly kind: "native"; readonly platform: InstallPlatform }
  /** No dialog exists. All the page can do is describe the menu path. */
  | { readonly kind: "manual"; readonly platform: "ios" };

const HIDDEN: InstallOffer = { kind: "hidden" };

/**
 * What to show, given the platform and whether a native handle exists.
 *
 * The last line is the interesting one, and it is deliberate silence. Note
 * what reaches it: not just `other`, but every `desktop` that never fired
 * `beforeinstallprompt` — which is how a non-Chromium laptop is recognised
 * here, since the event is a far better test of "has a dialog we can open"
 * than any user-agent string. Those browsers are of two kinds and both want
 * silence. Some have an install path we cannot name with confidence: desktop
 * Safari gained one in version 17 (Add to Dock), under a wording and in a menu
 * this page has no way to check. Others have none to name at all: desktop
 * Firefox ships no install command on the release channel — the Firefox that
 * does is the Android one, whose UA lands in `android` and gets a real button
 * the moment the event arrives. Instructions that name a menu item the visitor
 * cannot find are worse than no instructions, because they cost the trust that
 * would have made the *right* explanation land. So Chromium gets a real
 * button; iOS gets steps that have been the same for a decade; everyone else
 * is left with the browser's own install affordance, which they already have —
 * and which ../lib/install-prompt.ts is careful not to suppress on their behalf.
 *
 * Pure and exported for the tests — this is the whole policy in three lines.
 */
export function describeOffer(
  platform: InstallPlatform,
  hasNativePrompt: boolean,
): InstallOffer {
  if (hasNativePrompt) return { kind: "native", platform };
  if (platform === "ios") return { kind: "manual", platform };
  return HIDDEN;
}

/** What answering the browser's own dialog costs the visitor. */
export type OutcomeCost =
  /** Installed. The offer is done for good. */
  | "retire"
  /** A real no. A strike, and the snooze in ../lib/install-state.ts. */
  | "snooze"
  /** Take the panel down for this session and charge nothing. */
  | "close";

/**
 * Which of those three an outcome earns.
 *
 * The one worth spelling out is `unavailable`, which is not an answer at all:
 * `promptInstall` reports it when the handle was already spent or the browser
 * refused to open the dialog, so nothing was ever put in front of the visitor
 * to answer. Recording that as a dismissal — a strike, plus thirty days of
 * silence — would charge them for a decision they were never offered, and from
 * where they sit the only visible event was a button that did nothing. The
 * panel still comes down, because a button that does nothing is worse on
 * screen than off it, but it costs no more than this session.
 *
 * Pure and exported for the tests, like `describeOffer` above.
 */
export function costOfOutcome(outcome: InstallOutcome): OutcomeCost {
  if (outcome === "accepted") return "retire";
  if (outcome === "dismissed") return "snooze";
  return "close";
}

export interface InstallOfferControls {
  readonly offer: InstallOffer;
  /** Run the browser's install dialog. Only meaningful for a native offer. */
  readonly install: () => void;
  /** Not now, in as many words — spends a strike in ../lib/install-state.ts. */
  readonly dismiss: () => void;
  /**
   * Take the panel down for this session without spending a strike — for a
   * closing that is not necessarily an answer about installing.
   */
  readonly close: () => void;
}

// `recordLoad` counts a document load, not a mount, so it may run at most
// once per document however many times the mount effect below fires. Module
// scope rather than a ref: a real page load re-evaluates this module from
// scratch, which is exactly the reset the counter needs, while a ref would
// not survive Strict Mode's dev-only mount → cleanup → mount replay of that
// same effect — the replay is what would otherwise double-count load one and
// put the offer on screen a full visit early, breaking `MIN_LOADS`.
let loadRecordedThisDocument = false;

/**
 * The offer, for one app's install record.
 *
 * `store` must be the same object the app passed to `watchInstallability` —
 * one module-scope `createInstallStateStore` per app. It is a dependency of
 * the callbacks and effects below, so a store built during render would
 * re-run the mount effect on every render.
 */
export function useInstallOffer(
  store: InstallStateStore,
): InstallOfferControls {
  // `due` gates on storage and the clock; `platform` on the browser. Both,
  // plus `closed`, start at the value that renders nothing, so the first
  // client render matches the prerendered markup. `hasNativePrompt` starts
  // the same way, but it isn't local state — it comes from the external
  // store read below, whose own comment covers why.
  const [due, setDue] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>("other");
  const [closed, setClosed] = useState(false);

  // ../lib/install-prompt.ts is an external store, so it is read as one (react-
  // useeffect-discipline 3.3). The server snapshot is false for both: it is
  // what the prerender saw, and what hydration renders with.
  //
  // `beforeinstallprompt` fires before hydration far more often than not, so
  // the first client snapshot usually already holds the handle — which the
  // subscription alone, arriving after the event, would never report.
  const hasNativePrompt = useSyncExternalStore(
    subscribeInstallability,
    canPromptNatively,
    () => false,
  );
  // The tab that just installed the app is still a normal browser tab, so no
  // display-mode check will ever notice. `appinstalled` is the only signal,
  // and the offer has to come down on it. ../lib/install-prompt.ts records the
  // retirement in storage itself, where the event lands.
  const installed = useSyncExternalStore(
    subscribeInstallability,
    wasInstalledThisSession,
    () => false,
  );

  useEffect(() => {
    // Already the installed app: nothing to offer, and — importantly — no
    // load counted. Otherwise every launch from the home screen would spend
    // the counter that a browser visit is supposed to be earning.
    if (isRunningInstalled()) return;

    if (!loadRecordedThisDocument) {
      loadRecordedThisDocument = true;
      store.recordLoad();
    }
    if (!isOfferDue(store.read())) return;

    // The platform is read here, with `due`, rather than synchronously at the
    // top of the effect: nothing reads it until `due` is true, and a setState
    // made synchronously in an effect is a second render for nothing
    // (react/set-state-in-effect).
    const timer = setTimeout(() => {
      setPlatform(currentPlatform());
      setDue(true);
    }, INSTALL_TUNING.APPEAR_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [store]);

  const install = useCallback(() => {
    void promptInstall().then((outcome) => {
      // Three outcomes, three prices — see `costOfOutcome` above. "dismissed"
      // is a no to the browser's dialog and not to the site, so it snoozes
      // rather than retiring; "unavailable" means no dialog ever opened, and
      // is charged for accordingly, which is to say not at all. The panel
      // comes down in every case: the handle is spent either way, and a button
      // that can no longer do anything should not be sitting there.
      const cost = costOfOutcome(outcome);
      if (cost === "retire") store.retire();
      else if (cost === "snooze") store.recordDismissal();
      setClosed(true);
    });
  }, [store]);

  const dismiss = useCallback(() => {
    store.recordDismissal();
    setClosed(true);
  }, [store]);

  // Takes the panel down without the dismissal's strike — see `close` in
  // InstallOfferControls above, and apps/retells/src/install/InstallOffer.tsx
  // for the one caller this exists for (Escape). `shown`'s effect below still stamps `askedAt`
  // on its own, so the panel is snoozed the same as any other closing; only
  // the dismissal count is untouched.
  const close = useCallback(() => {
    setClosed(true);
  }, []);

  const offer =
    due && !closed && !installed
      ? describeOffer(platform, hasNativePrompt)
      : HIDDEN;

  // Being shown is itself an asking, and has to be written down as one.
  //
  // Nothing else would record it reliably. The timestamp in
  // ../lib/install-state.ts is otherwise stamped only by the X or by an
  // answer to the browser's dialog — `close` (Escape; see
  // apps/retells/src/install/InstallOffer.tsx) deliberately does not, since
  // it is not always an answer about installing — so an offer that is simply
  // left alone would return on every single load — and on iOS, where there
  // is no `appinstalled` event and a Safari tab cannot see the home-screen
  // copy of itself (see `isRunningInstalled` in ../lib/install-platform.ts),
  // that includes the visitor who followed all three steps and installed it
  // successfully. They would be asked again every visit for the
  // rest of the year unless they thought to dismiss a panel they had obeyed.
  //
  // Keyed on `offer.kind` rather than on `due`, because those are different
  // claims: `due` says storage and the clock permit an asking, while a `kind`
  // other than `hidden` says one resolved to something to put on screen. A
  // desktop browser that is due but has no native handle shows nothing, and
  // must not be recorded as having asked.
  //
  // The effect is where this belongs, not the render that computed `offer`:
  // the first client render has to match the prerendered markup, and the write
  // is a side effect either way.
  const shown = offer.kind !== "hidden";
  useEffect(() => {
    if (!shown) return;
    store.recordImpression();
  }, [shown, store]);

  return { offer, install, dismiss, close };
}
